// ============================================================================
// todo-client-mod -- TodoGroupSelector
//
// 分组侧边栏/下拉选择器组件。
// 展示分组列表（含 todoCount），支持选中、创建、管理分组。
// 响应式设计：移动端自动切换为下拉选择器。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { PlusIcon, FolderIcon, InboxIcon } from './icons.js';
import { useGroups } from '../hooks/use-groups.js';
import { radius, typography, transition } from '../styles/theme.js';
export function TodoGroupSelector({ selectedGroupId, onSelect, onManage, compact = false, }) {
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const { groups, loading, error } = useGroups();
    if (loading) {
        return React.createElement('div', {
            style: {
                fontSize: typography.fontSize.sm,
                color: theme.text.tertiary,
                padding: '12px',
            },
        }, '加载分组中...');
    }
    if (error) {
        return React.createElement('div', {
            style: {
                fontSize: typography.fontSize.sm,
                color: theme.status.danger,
                padding: '12px',
            },
        }, error);
    }
    // 紧凑模式 或 移动端 → 下拉选择器
    if (compact || isMobile) {
        return React.createElement('select', {
            value: selectedGroupId ?? '',
            onChange: (e) => onSelect(e.target.value || undefined),
            style: {
                padding: '8px 12px',
                border: `1px solid ${theme.border.default}`,
                borderRadius: radius.md,
                fontSize: typography.fontSize.base,
                outline: 'none',
                width: '100%',
                backgroundColor: theme.bg.secondary,
                color: theme.text.primary,
                transition: `border-color ${transition.fast}`,
                cursor: 'pointer',
            },
        }, React.createElement('option', { value: '' }, '全部分组'), ...groups.map(g => React.createElement('option', { key: g._id, value: g._id }, `${g.name} (${g.todoCount})`)));
    }
    const plusIconNode = React.createElement(PlusIcon);
    const inboxIconNode = React.createElement(InboxIcon);
    const folderIconNode = React.createElement(FolderIcon);
    const totalCount = groups.reduce((sum, g) => sum + g.todoCount, 0);
    const itemBase = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        borderRadius: radius.md,
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: typography.fontSize.sm,
        color: theme.text.secondary,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: `background-color ${transition.fast}, color ${transition.fast}`,
    };
    const allItem = React.createElement('button', {
        onClick: () => onSelect(undefined),
        style: {
            ...itemBase,
            ...(selectedGroupId === undefined ? {
                backgroundColor: theme.interaction.selected,
                color: theme.status.primary,
                fontWeight: typography.fontWeight.medium,
            } : {}),
        },
    }, folderIconNode, React.createElement('span', null, '全部'), React.createElement('span', { style: { fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium, color: theme.text.tertiary, marginLeft: 'auto' } }, totalCount));
    const groupItems = groups.map(group => {
        const isSelected = selectedGroupId === group._id;
        const icon = group.isDefault ? inboxIconNode : folderIconNode;
        return React.createElement('button', {
            key: group._id,
            onClick: () => onSelect(group._id),
            style: {
                ...itemBase,
                ...(isSelected ? {
                    backgroundColor: theme.interaction.selected,
                    color: theme.status.primary,
                    fontWeight: typography.fontWeight.medium,
                } : {}),
            },
        }, icon, React.createElement('span', {
            style: {
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        }, group.name), React.createElement('span', {
            style: {
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.medium,
                color: isSelected ? theme.status.primary : theme.text.tertiary,
                opacity: 0.8,
                marginLeft: 'auto',
            },
        }, group.todoCount));
    });
    const addButton = React.createElement('button', {
        onClick: onManage,
        style: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            marginTop: '8px',
            border: `1px dashed ${theme.border.default}`,
            borderRadius: radius.md,
            backgroundColor: 'transparent',
            fontSize: typography.fontSize.sm,
            color: theme.text.tertiary,
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
            transition: `border-color ${transition.fast}, color ${transition.fast}, background-color ${transition.fast}`,
        },
    }, plusIconNode, React.createElement('span', null, '新分组'));
    return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '180px' } }, React.createElement('div', {
        style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '4px 8px',
            marginBottom: '4px',
        },
    }, React.createElement('span', {
        style: {
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            color: theme.text.tertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
    }, '分组'), React.createElement('button', {
        onClick: onManage,
        style: {
            background: 'none',
            border: 'none',
            fontSize: '16px',
            color: theme.text.tertiary,
            cursor: 'pointer',
            padding: '2px 4px',
            borderRadius: radius.sm,
            transition: `background-color ${transition.fast}, color ${transition.fast}`,
        },
        title: '管理分组',
    }, '≡')), allItem, ...groupItems, addButton);
}
//# sourceMappingURL=todo-group-selector.js.map